/*istanbul ignore file */
/* global globalThis */
import React, { useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import Cropper from "react-easy-crop";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
  Stack,
  Typography,
} from "@mui/material";
import CropIcon from "@mui/icons-material/Crop";
import ZoomInIcon from "@mui/icons-material/ZoomIn";

const CROP_FRAME_OUTER_GAP = 48;
const DEFAULT_CROP_FRAME_BOUNDS = { width: 900, height: 520 };
const MAX_CROP_FRAME_WIDTH = 900;
const DIALOG_WIDTH_BUFFER = 100;

const clampZoom = (value) => Math.max(0.1, Math.min(1, value));

const getZoomToFitCropFrame = (sourceWidth, sourceHeight, cropFrameSize) => {
  const sourceAspect = sourceWidth / sourceHeight;
  const containerWidth = cropFrameSize.width + CROP_FRAME_OUTER_GAP;
  const containerHeight = cropFrameSize.height + CROP_FRAME_OUTER_GAP;
  const containerAspect = containerWidth / containerHeight;
  let mediaWidthAtZoomOne = containerWidth;
  if (containerAspect > sourceAspect) {
    mediaWidthAtZoomOne = containerHeight * sourceAspect;
  }
  const mediaHeightAtZoomOne = mediaWidthAtZoomOne / sourceAspect;

  return clampZoom(
    Math.min(
      cropFrameSize.width / mediaWidthAtZoomOne,
      cropFrameSize.height / mediaHeightAtZoomOne,
    ),
  );
};

const getInitialZoom = ({
  sourceTooSmall,
  containRatio,
  coverRatio,
  isSameOutputAspect,
  sourceWidth,
  sourceHeight,
  cropFrameSize,
}) => {
  if (sourceTooSmall) return clampZoom(containRatio / coverRatio);
  if (isSameOutputAspect) {
    return getZoomToFitCropFrame(sourceWidth, sourceHeight, cropFrameSize);
  }
  return 1;
};

const getResponsiveCropFrameSize = (
  bounds,
  aspectRatio,
  targetWidth,
  targetHeight,
) => {
  const maxFrameHeight = Math.max(1, bounds.height - CROP_FRAME_OUTER_GAP);

  let width = Math.min(
    MAX_CROP_FRAME_WIDTH,
    Math.max(1, targetWidth || bounds.width),
  );
  let height = width / aspectRatio;

  if (targetWidth && targetHeight) {
    height = targetHeight * (width / targetWidth);
  }

  if (height > maxFrameHeight) {
    height = maxFrameHeight;
    width = height * aspectRatio;
  }

  return {
    width: Math.round(width),
    height: Math.round(height),
  };
};

/** Draws the cropped region onto a canvas and returns a Blob at the target dimensions. */
async function getCroppedImg(
  imageSrc,
  pixelCrop,
  outputWidth,
  outputHeight,
  mimeType,
) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => {
      const canvas = document.createElement("canvas");
      canvas.width = outputWidth;
      canvas.height = outputHeight;
      const ctx = canvas.getContext("2d");
      // Paint a solid white background so any region outside the source image
      // (when the user zoomed out beyond the image) is rendered as white
      // instead of transparent black in the JPEG output.
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, outputWidth, outputHeight);

      const scaleX = outputWidth / pixelCrop.width;
      const scaleY = outputHeight / pixelCrop.height;

      const srcX = Math.max(0, pixelCrop.x);
      const srcY = Math.max(0, pixelCrop.y);
      const srcW = Math.min(
        image.naturalWidth - srcX,
        pixelCrop.width - (srcX - pixelCrop.x),
      );
      const srcH = Math.min(
        image.naturalHeight - srcY,
        pixelCrop.height - (srcY - pixelCrop.y),
      );

      if (srcW > 0 && srcH > 0) {
        const destX = (srcX - pixelCrop.x) * scaleX;
        const destY = (srcY - pixelCrop.y) * scaleY;
        const destW = srcW * scaleX;
        const destH = srcH * scaleY;
        ctx.drawImage(
          image,
          srcX,
          srcY,
          srcW,
          srcH,
          destX,
          destY,
          destW,
          destH,
        );
      }

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Canvas toBlob failed"));
            return;
          }
          resolve(blob);
        },
        mimeType,
        0.92,
      );
    });
    image.addEventListener("error", reject);
    image.src = imageSrc;
  });
}

/**
 * A modal image cropper built on react-easy-crop.
 *
 * Props:
 *   open          – whether the dialog is visible
 *   imageSrc      – base64 / object-URL of the raw image to crop
 *   aspectRatio   – target width / height ratio (e.g. 2560/1600)
 *   targetWidth   – output canvas width in px
 *   targetHeight  – output canvas height in px
 *   fileName      – filename to give the resulting File object
 *   mimeType      – MIME type (defaults to "image/jpeg")
 *   onCancel      – called when user dismisses without cropping
 *   onConfirm     – called with the cropped File object
 */
const ImageCropperModal = ({
  open,
  imageSrc,
  aspectRatio,
  targetWidth,
  targetHeight,
  fileName,
  mimeType,
  onCancel,
  onConfirm,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [minZoom, setMinZoom] = useState(0.25);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSourceTooSmall, setIsSourceTooSmall] = useState(false);
  const [sourceDims, setSourceDims] = useState(null);
  const cropperContainerRef = useRef(null);
  const maxZoom = 4;
  const [cropFrameSize, setCropFrameSize] = useState(() =>
    getResponsiveCropFrameSize(
      DEFAULT_CROP_FRAME_BOUNDS,
      aspectRatio,
      targetWidth,
      targetHeight,
    ),
  );

  React.useEffect(() => {
    setCropFrameSize(
      getResponsiveCropFrameSize(
        DEFAULT_CROP_FRAME_BOUNDS,
        aspectRatio,
        targetWidth,
        targetHeight,
      ),
    );
  }, [aspectRatio, targetWidth, targetHeight]);

  React.useEffect(() => {
    const container = cropperContainerRef.current;
    const browserWindow = globalThis.window;
    if (!container || !browserWindow) return undefined;

    const updateCropFrameSize = () => {
      const { width } = container.getBoundingClientRect();
      if (!width) return;

      const viewportHeight = Math.min(
        DEFAULT_CROP_FRAME_BOUNDS.height,
        Math.max(260, browserWindow.innerHeight - 280),
      );

      const nextSize = getResponsiveCropFrameSize(
        { width, height: viewportHeight || DEFAULT_CROP_FRAME_BOUNDS.height },
        aspectRatio,
        targetWidth,
        targetHeight,
      );
      setCropFrameSize((currentSize) =>
        currentSize.width === nextSize.width &&
        currentSize.height === nextSize.height
          ? currentSize
          : nextSize,
      );
    };

    updateCropFrameSize();
    browserWindow.addEventListener("resize", updateCropFrameSize);

    if (typeof ResizeObserver === "undefined") {
      return () =>
        browserWindow.removeEventListener("resize", updateCropFrameSize);
    }

    const resizeObserver = new ResizeObserver(updateCropFrameSize);
    resizeObserver.observe(container);
    return () => {
      resizeObserver.disconnect();
      browserWindow.removeEventListener("resize", updateCropFrameSize);
    };
  }, [aspectRatio, open, targetWidth, targetHeight]);

  // When a new image is loaded, start large/enough images at crop-cover zoom.
  // Only undersized images are allowed to zoom out so their full image is visible.
  React.useEffect(() => {
    if (!imageSrc) return;
    const img = new Image();
    img.onload = () => {
      setSourceDims({ width: img.naturalWidth, height: img.naturalHeight });
      const sourceTooSmall =
        img.naturalWidth < targetWidth || img.naturalHeight < targetHeight;
      setIsSourceTooSmall(sourceTooSmall);

      const widthRatio = img.naturalWidth / targetWidth;
      const heightRatio = img.naturalHeight / targetHeight;
      const containRatio = Math.min(widthRatio, heightRatio);
      const coverRatio = Math.max(widthRatio, heightRatio);
      const sourceAspect = img.naturalWidth / img.naturalHeight;
      const targetAspect = targetWidth / targetHeight;
      const isSameOutputAspect = Math.abs(sourceAspect - targetAspect) < 0.01;
      const computedMin = getInitialZoom({
        sourceTooSmall,
        containRatio,
        coverRatio,
        isSameOutputAspect,
        sourceWidth: img.naturalWidth,
        sourceHeight: img.naturalHeight,
        cropFrameSize,
      });
      setMinZoom(computedMin);
      setZoom(computedMin);
      setCrop({ x: 0, y: 0 });
    };
    img.src = imageSrc;
  }, [cropFrameSize, imageSrc, targetWidth, targetHeight]);

  const handleCropComplete = useCallback((_, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!croppedAreaPixels) return;
    setIsProcessing(true);
    try {
      const effectiveMime = mimeType || "image/jpeg";
      const blob = await getCroppedImg(
        imageSrc,
        croppedAreaPixels,
        targetWidth,
        targetHeight,
        effectiveMime,
      );
      const croppedFile = new File([blob], fileName, { type: effectiveMime });
      onConfirm(croppedFile);
    } catch {
      onCancel();
    } finally {
      setIsProcessing(false);
    }
  }, [
    croppedAreaPixels,
    imageSrc,
    targetWidth,
    targetHeight,
    mimeType,
    fileName,
    onConfirm,
    onCancel,
  ]);

  return (
    <Dialog
      open={open}
      maxWidth={false}
      onClose={onCancel}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 2 },
          width: {
            xs: "100vw",
            sm:
              cropFrameSize.width + CROP_FRAME_OUTER_GAP + DIALOG_WIDTH_BUFFER,
          },
          minWidth: { xs: "100vw", sm: 520 },
          maxWidth: { xs: "100vw", sm: "calc(100vw - 32px)" },
          maxHeight: { xs: "100vh", sm: "94vh" },
          m: { xs: 0, sm: 2 },
        },
      }}
    >
      <DialogTitle sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 1.5, sm: 2 }, pb: 0 }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <CropIcon color="primary" />
          <Box>
            <Typography variant="h6" component="span">
              Crop Image
            </Typography>
            <Typography variant="body2" color="text.secondary" display="block">
              Output: {targetWidth}×{targetHeight}px — drag to reposition,
              scroll / slider to zoom
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          px: { xs: 1.5, sm: 3 },
          pb: 0,
          overflow: "hidden",
        }}
      >
        {/* Cropper canvas — keeps the target aspect visible; narrow screens
            scroll horizontally instead of clipping the crop area. */}
        <Box
          ref={cropperContainerRef}
          sx={{
            width: "100%",
            flex: "1 1 auto",
            minHeight: 0,
            overflowX: "auto",
            overflowY: "auto",
            mt: 1,
            pb: 1,
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: cropFrameSize.width + CROP_FRAME_OUTER_GAP,
              height: cropFrameSize.height + CROP_FRAME_OUTER_GAP,
              minWidth: cropFrameSize.width + CROP_FRAME_OUTER_GAP,
              mx: "auto",
              background: "#1a1a2e",
              borderRadius: 1,
              overflow: "hidden",
              p: 2,
              "& .reactEasyCrop_CropArea": {
                boxShadow:
                  "0 0 0 9999px rgba(0,0,0,0.55), inset 0 0 0 2px rgb(25, 118, 210) !important",
              },
            }}
          >
            {imageSrc && (
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                minZoom={minZoom}
                maxZoom={maxZoom}
                restrictPosition={false}
                aspect={aspectRatio}
                cropSize={cropFrameSize}
                objectFit="contain"
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={handleCropComplete}
                style={{
                  cropAreaStyle: {
                    border: "none",
                    boxShadow:
                      "0 0 0 9999px rgba(0,0,0,0.55), inset 0 0 0 1px rgb(25, 118, 210)",
                  },
                }}
              />
            )}
          </Box>
        </Box>

        {/* Zoom slider */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{ mt: { xs: 1, sm: 2 }, px: 1 }}
        >
          <ZoomInIcon fontSize="small" color="action" />
          <Typography variant="caption" color="text.secondary" minWidth={36}>
            Zoom
          </Typography>
          <Slider
            value={zoom}
            min={minZoom}
            max={maxZoom}
            step={0.01}
            size="small"
            onChange={(_, z) => setZoom(z)}
            aria-label="Zoom"
            sx={{ flex: 1 }}
          />
          <Typography variant="caption" color="text.secondary" minWidth={32}>
            {zoom.toFixed(1)}×
          </Typography>
        </Stack>

        {isSourceTooSmall && sourceDims && (
          <Typography
            variant="caption"
            sx={{ display: "block", mt: 1, px: 1, color: "warning.main" }}
          >
            Heads up: your image is {sourceDims.width}×{sourceDims.height}px,
            smaller than the recommended {targetWidth}×{targetHeight}px. The
            output will be padded with white where the image doesn't cover the
            crop area, and may appear stretched if zoomed in.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1, sm: 2 } }}>
        <Button onClick={onCancel} variant="outlined" disabled={isProcessing}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={isProcessing || !croppedAreaPixels}
          startIcon={<CropIcon />}
        >
          {isProcessing ? "Processing…" : "Crop & Use"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ImageCropperModal.propTypes = {
  open: PropTypes.bool.isRequired,
  imageSrc: PropTypes.string,
  aspectRatio: PropTypes.number.isRequired,
  targetWidth: PropTypes.number.isRequired,
  targetHeight: PropTypes.number.isRequired,
  fileName: PropTypes.string.isRequired,
  mimeType: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

ImageCropperModal.defaultProps = {
  imageSrc: null,
  mimeType: "image/jpeg",
};

export default ImageCropperModal;
